import React, { useEffect, useMemo, useState } from "react";
import Button from "../../ui/Button";
import { typography } from "../../ui/tokens";
import { StatusBadge } from "../../ui";
import { getExecutiveRiskDashboard } from "../../api/riskDashboard.api";

const defaultParams = { limit: 5, months: 6 };

export default function ExecutiveRiskDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    getExecutiveRiskDashboard(defaultParams)
      .then((res) => setData(res.data))
      .catch(() => setError("Unable to load executive risk dashboard."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const kpis = data?.kpis;
  const topLists = data?.top_lists;
  const trends = data?.trends;

  const bucketLabels = useMemo(
    () => (trends?.risk_counts || []).map((item) => item.bucket),
    [trends]
  );

  return (
    <div style={{ display: "grid", gap: "16px" }}>
      <section style={headerCard}>
        <div>
          <h2 style={{ margin: 0, marginBottom: "6px", fontSize: typography.size.xl }}>Executive Risk Dashboard</h2>
          <div style={{ color: "#6b7280", fontSize: typography.size.sm }}>Executive View (Read-only)</div>
        </div>
        <Button onClick={load} variant="secondary" style={headerButton}>
          Refresh
        </Button>
      </section>

      {loading && <div style={mutedText}>Loading executive risk dashboard...</div>}
      {error && <div style={{ color: "#b91c1c", fontSize: typography.size.sm }}>{error}</div>}

      {!loading && !error && (
        <>
          <section style={kpiGrid}>
            <KpiCard
              title="Total Active Risks"
              value={kpis?.total_active ?? 0}
              helper="Open risks excluding archives."
              onClick={() => window.dispatchEvent(new CustomEvent("navigateModule", { detail: "risks" }))}
            />
            <KpiCard
              title="High Risks"
              value={kpis?.high_risks ?? 0}
              helper="Residual score at or above threshold."
            />
            <KpiCard
              title="Appetite Breaches"
              value={kpis?.appetite_breaches ?? 0}
              helper="Risks exceeding appetite thresholds."
            />
            <KpiCard
              title="Overdue Reviews"
              value={kpis?.overdue_reviews ?? 0}
              helper="Next review date has passed."
            />
            <KpiCard
              title="Overdue Treatments"
              value={kpis?.overdue_treatments ?? 0}
              helper="Treatment due date has passed."
            />
          </section>

          <section style={gridTwo}>
            <ListCard
              title="Top Risks by Residual Score"
              helper="Highest residual exposure."
              items={topLists?.top_by_residual || []}
              emptyText="No risks available."
            />
            <ListCard
              title="Top Appetite Breaches"
              helper="Risks exceeding appetite."
              items={topLists?.top_appetite_breaches || []}
              emptyText="No appetite breaches."
            />
            <ListCard
              title="Top Overdue Treatments"
              helper="Treatment plans past due."
              items={topLists?.top_overdue_treatments || []}
              emptyText="No overdue treatments."
            />
          </section>

          <section style={gridTwo}>
            <TrendCard
              title="Risk Counts"
              rows={(trends?.risk_counts || []).map((row, index) => ({
                label: bucketLabels[index],
                left: `Created ${row.created}`,
                right: `Closed ${row.closed}`,
              }))}
              emptyText="No trend data."
            />
            <TrendCard
              title="Appetite Breach Trend"
              rows={(trends?.appetite_breaches || []).map((row, index) => ({
                label: bucketLabels[index],
                left: `Breaches ${row.breaches}`,
                right: "",
              }))}
              emptyText="No appetite trend data."
            />
            <TrendCard
              title="Review Compliance"
              rows={(trends?.review_compliance || []).map((row, index) => ({
                label: bucketLabels[index],
                left: `On-time ${row.on_time}`,
                right: `Overdue ${row.overdue}`,
              }))}
              emptyText="No review compliance data."
            />
          </section>

          <section style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
              <div>
                <div style={{ fontWeight: typography.weight.bold }}>Risk Heatmap</div>
                <div style={mutedText}>
                  {kpis?.appetite_breaches ?? 0} risks exceed appetite.
                </div>
              </div>
              <Button
                onClick={() => window.dispatchEvent(new CustomEvent("navigateModule", { detail: "heatmap" }))}
                variant="secondary"
              >
                Open Heatmap
              </Button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function KpiCard({ title, value, helper, onClick }) {
  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
        <div>
          <div style={{ fontSize: typography.size.xs, color: "#6b7280" }}>{title}</div>
          <div style={{ fontSize: typography.size["2xl"], fontWeight: typography.weight.bold, marginTop: "6px" }}>{value}</div>
        </div>
        {onClick && (
          <Button onClick={onClick} variant="secondary" style={tinyButton}>
            View
          </Button>
        )}
      </div>
      <div style={{ fontSize: typography.size.xs, color: "#6b7280", marginTop: "10px" }}>{helper}</div>
    </div>
  );
}

function ListCard({ title, helper, items, emptyText }) {
  return (
    <div style={cardStyle}>
      <div style={{ fontWeight: typography.weight.bold }}>{title}</div>
      <div style={{ fontSize: typography.size.xs, color: "#6b7280", marginBottom: "8px" }}>{helper}</div>
      {items.length === 0 && <div style={mutedText}>{emptyText}</div>}
      {items.map((item) => (
        <div key={item.id} style={listRow}>
          <div>
            <div style={{ fontSize: typography.size.sm, fontWeight: typography.weight.medium }}>{item.name}</div>
            <div style={{ fontSize: typography.size.xs, color: "#6b7280" }}>
              {item.category || "Uncategorized"} · {item.status || "Unknown"}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" }}>
            {typeof item.residual_score === "number" && (
              <span style={{ fontSize: typography.size.xs, fontWeight: typography.weight.medium }}>{item.residual_score}</span>
            )}
            {item.appetite_status && <StatusBadge value={item.appetite_status} />}
            {item.treatment_status && <StatusBadge value={item.treatment_status} />}
          </div>
        </div>
      ))}
    </div>
  );
}

function TrendCard({ title, rows, emptyText }) {
  return (
    <div style={cardStyle}>
      <div style={{ fontWeight: typography.weight.bold }}>{title}</div>
      {rows.length === 0 && <div style={mutedText}>{emptyText}</div>}
      {rows.map((row) => (
        <div key={row.label} style={trendRow}>
          <div style={{ fontSize: typography.size.xs, color: "#6b7280" }}>{row.label}</div>
          <div style={{ display: "flex", gap: "8px", fontSize: typography.size.xs }}>
            <span>{row.left}</span>
            {row.right && <span style={{ color: "#6b7280" }}>{row.right}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

const headerCard = {
  background: "#fff",
  borderRadius: "12px",
  border: "1px solid #e5e7eb",
  padding: "16px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
  flexWrap: "wrap",
};

const headerButton = {
  padding: "6px 10px",
  borderRadius: "8px",
  border: "1px solid #d1d5db",
  background: "#f3f4f6",
};

const mutedText = { color: "#6b7280", fontSize: typography.size.sm };

const cardStyle = {
  background: "white",
  borderRadius: "12px",
  border: "1px solid #e5e7eb",
  padding: "14px",
  display: "flex",
  flexDirection: "column",
  gap: "6px",
};

const kpiGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: "12px",
};

const gridTwo = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: "12px",
};

const listRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "10px",
  padding: "8px 10px",
  borderRadius: "8px",
  border: "1px solid #e5e7eb",
  background: "#f9fafb",
  marginBottom: "8px",
};

const trendRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderBottom: "1px dashed #e5e7eb",
  padding: "6px 0",
};

const tinyButton = {
  padding: "4px 8px",
  borderRadius: "8px",
  border: "1px solid #d1d5db",
  background: "#f3f4f6",
};
